import React from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowDown, Terminal } from "lucide-react";
import portfolioData from "../../data/mock";

const HeroSection = () => {
  const { personalInfo } = portfolioData;

  const scrollToAbout = () => {
    const el = document.querySelector("#about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-ink-950"
    >
      {/* Subtle grid background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

      {/* Subtle radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-500/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-28 lg:pt-24 w-full">
        <div className="flex flex-col-reverse lg:flex-row items-center lg:items-center lg:justify-between gap-10 lg:gap-20">
          {/* Left: Text content */}
          <div className="flex-1 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ink-800 bg-ink-900/50 text-slate-300 text-sm mb-10"
            >
              <Terminal size={14} className="text-ember-400" />
              <span>Available for new opportunities</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-[1.1] mb-6"
            >
              {personalInfo.name.split(" ").map((word, i) => (
                <span key={i}>
                  {i === 0 ? (
                    <span className="text-white">{word} </span>
                  ) : i === 1 ? (
                    <span className="text-brand-400">{word} </span>
                  ) : (
                    <span className="text-slate-300">{word} </span>
                  )}
                </span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="text-lg sm:text-xl text-slate-300 max-w-2xl lg:max-w-lg mb-4 font-light"
            >
              {personalInfo.title}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="flex items-center justify-center lg:justify-start gap-2 text-slate-400 text-sm mb-10"
            >
              <MapPin size={14} />
              <span>{personalInfo.location}</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4"
            >
              <a
                href={`mailto:${personalInfo.email}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-500 hover:bg-brand-400 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
              >
                Get in Touch
              </a>
              <a
                href={personalInfo.github}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 border border-ink-700 hover:border-slate-400 text-slate-200 hover:text-white rounded-lg transition-all duration-200 text-sm"
              >
                View GitHub
              </a>
            </motion.div>
          </div>

          {/* Right: Profile photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="flex-shrink-0"
          >
            <div className="relative group">
              {/* Glow ring behind the image */}
              <div className="absolute -inset-1.5 rounded-full bg-brand-500/20 blur-lg group-hover:bg-brand-500/30 transition-all duration-500 opacity-70" />
              {personalInfo.avatar ? (
                <img
                  src={personalInfo.avatar}
                  alt={personalInfo.name}
                  className="relative w-40 h-40 sm:w-44 sm:h-44 lg:w-48 lg:h-48 rounded-full aspect-square object-cover border-2 border-ink-800/80 shadow-[0_0_40px_rgba(76,141,255,0.18)] group-hover:shadow-[0_0_60px_rgba(76,141,255,0.28)] transition-shadow duration-500"
                />
              ) : (
                <div
                  aria-label={personalInfo.name}
                  className="relative w-40 h-40 sm:w-44 sm:h-44 lg:w-48 lg:h-48 rounded-full aspect-square flex items-center justify-center bg-ink-900 border-2 border-ink-800/80 shadow-[0_0_40px_rgba(76,141,255,0.18)] group-hover:shadow-[0_0_60px_rgba(76,141,255,0.28)] transition-shadow duration-500"
                >
                  <span className="text-5xl lg:text-6xl font-bold tracking-tight text-brand-400 select-none">
                    {personalInfo.name
                      .split(" ")
                      .map((w) => w[0])
                      .join("")}
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollToAbout}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 text-slate-400 hover:text-brand-400 transition-colors cursor-pointer"
        aria-label="Scroll down"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <ArrowDown size={20} />
        </motion.div>
      </motion.button>
    </section>
  );
};

export default HeroSection;
